import { ApiError } from "../api/errors";
import { advanceCursor, FEED_PAGE_SIZE, type FeedPage } from "./feedItems";

/**
 * Цикл long-poll ленты: GET /feed?after=<seq>&wait=1 по кругу.
 *
 * - первый запрос и догонялки после полной страницы — без ожидания: история нужна сразу;
 * - ошибка — пауза с экспоненциальным ростом и разбросом (сотня вкладок после падения
 *   сервера не должна приходить ровно в одну секунду), курсор при этом не теряется;
 * - пустой ответ на wait=1, пришедший сразу, — это не «новостей нет за 25 с», а отказ
 *   (потолок ожиданий на подписчика: открыто больше трёх вкладок) или прокси, режущий
 *   long-poll: без паузы такой клиент крутил бы запросы без остановки;
 * - 401 и 404 — конец: сессия кончилась или героя нет, повтор не поможет;
 * - stop() обрывает текущий запрос и паузу: вкладка скрыта — соединение серверу не нужно.
 */

export interface FeedFetchInput {
  after: number;
  wait: boolean;
  signal: AbortSignal;
}

export interface FeedPollerDeps {
  fetchPage(input: FeedFetchInput): Promise<FeedPage>;
  /** Страница пришла; receivedAt — часы клиента в момент ответа (для подсказки «печатает»). */
  onPage(page: FeedPage, meta: { receivedAt: number; waited: boolean }): void;
  /** Сбой запроса, будет повтор через delayMs. */
  onError?(error: unknown, meta: { attempt: number; delayMs: number }): void;
  /** Цикл остановлен насовсем (401, 404). */
  onFatal?(error: unknown): void;
  /** Начинается обновление подписанных URL всей истории с after=0. */
  onRefresh?(): void;
  now(): number;
  sleep(ms: number, signal: AbortSignal): Promise<void>;
  random?(): number;
}

export interface FeedPollerOptions {
  cursor: number;
  /** Ответ на wait=1 быстрее этого — «отказ», а не таймаут ожидания. */
  minHoldMs?: number;
  errorBackoff?: Backoff;
  busyBackoff?: Backoff;
  refreshEveryMs?: number;
}

export interface Backoff {
  baseMs: number;
  maxMs: number;
}

export const ERROR_BACKOFF: Backoff = { baseMs: 1_000, maxMs: 30_000 };
export const BUSY_BACKOFF: Backoff = { baseMs: 1_500, maxMs: 15_000 };
export const MIN_HOLD_MS = 1_500;

/** Пауза перед попыткой attempt (с 1): base·2^(n−1), не больше max, разброс ±20%. */
export function backoffDelay(attempt: number, backoff: Backoff, random: () => number = Math.random): number {
  const exp = Math.min(backoff.maxMs, backoff.baseMs * 2 ** Math.max(0, attempt - 1));
  const jitter = 0.8 + 0.4 * Math.min(1, Math.max(0, random()));
  return Math.round(Math.min(backoff.maxMs, exp * jitter));
}

/** Ошибки, после которых спрашивать снова бессмысленно. */
export function isFatalFeedError(error: unknown): boolean {
  return error instanceof ApiError && (error.code === "unauthorized" || error.code === "not_found");
}

export function abortableSleep(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) return resolve();
    const timer = setTimeout(done, ms);
    function done() {
      clearTimeout(timer);
      signal.removeEventListener("abort", done);
      resolve();
    }
    signal.addEventListener("abort", done, { once: true });
  });
}

export class FeedPoller {
  private cursorValue: number;
  private controller: AbortController | null = null;
  private generation = 0;
  private readonly minHoldMs: number;
  private readonly errorBackoff: Backoff;
  private readonly busyBackoff: Backoff;
  private refreshedAt: number;

  constructor(
    private readonly deps: FeedPollerDeps,
    options: FeedPollerOptions,
  ) {
    this.cursorValue = options.cursor;
    this.minHoldMs = options.minHoldMs ?? MIN_HOLD_MS;
    this.errorBackoff = options.errorBackoff ?? ERROR_BACKOFF;
    this.busyBackoff = options.busyBackoff ?? BUSY_BACKOFF;
    this.refreshEveryMs = options.refreshEveryMs ?? Number.POSITIVE_INFINITY;
    this.refreshedAt = deps.now();
  }

  private readonly refreshEveryMs: number;

  refreshHistory(): void {
    this.stop();
    this.resetHistory();
    this.start();
  }

  private resetHistory(): void {
    this.cursorValue = 0;
    this.refreshedAt = this.deps.now();
    this.deps.onRefresh?.();
  }

  get cursor(): number {
    return this.cursorValue;
  }

  get running(): boolean {
    return this.controller !== null;
  }

  /** Запустить цикл; уже идёт — ничего. Первый запрос — без ожидания (догнать историю). */
  start(): void {
    if (this.controller) return;
    const controller = new AbortController();
    this.controller = controller;
    const generation = ++this.generation;
    void this.run(generation, controller.signal);
  }

  /** Остановить: текущий запрос и пауза обрываются, курсор остаётся. */
  stop(): void {
    this.controller?.abort();
    this.controller = null;
    this.generation += 1;
  }

  /** Спросить сейчас, не дожидаясь паузы после ошибки (вернулась сеть, вкладка). */
  restart(): void {
    this.stop();
    this.start();
  }

  /** Сбросить курсор (кеш не сошёлся с сервером): со следующего запроса — отсюда. */
  resetCursor(cursor: number): void {
    this.cursorValue = cursor;
  }

  private alive(generation: number): boolean {
    return generation === this.generation;
  }

  private async run(generation: number, signal: AbortSignal): Promise<void> {
    const random = this.deps.random ?? Math.random;
    let wait = false;
    let errors = 0;
    let busy = 0;
    while (this.alive(generation)) {
      if (this.deps.now() - this.refreshedAt >= this.refreshEveryMs) {
        this.resetHistory();
        wait = false;
      }
      const sentAt = this.deps.now();
      try {
        const page = await this.deps.fetchPage({ after: this.cursorValue, wait, signal });
        if (!this.alive(generation)) return;
        const receivedAt = this.deps.now();
        errors = 0;
        this.cursorValue = advanceCursor(this.cursorValue, page);
        this.deps.onPage(page, { receivedAt, waited: wait });
        const quickEmpty = wait && page.count === 0 && receivedAt - sentAt < this.minHoldMs;
        if (quickEmpty) {
          busy += 1;
          await this.deps.sleep(backoffDelay(busy, this.busyBackoff, random), signal);
        } else {
          busy = 0;
        }
        // Полная страница — за ней есть ещё: следующую без ожидания
        wait = page.count < FEED_PAGE_SIZE;
      } catch (error) {
        if (!this.alive(generation) || signal.aborted) return;
        if (isFatalFeedError(error)) {
          this.controller = null;
          this.generation += 1;
          this.deps.onFatal?.(error);
          return;
        }
        errors += 1;
        const delayMs = backoffDelay(errors, this.errorBackoff, random);
        this.deps.onError?.(error, { attempt: errors, delayMs });
        await this.deps.sleep(delayMs, signal);
        // После сбоя — сначала догнать без ожидания: пока ждали, могло прийти
        wait = false;
      }
    }
  }
}
